/**
 * @file CityHeroChips.jsx
 * @description The icon row under a city page's one CTA: what this provider gets on ABAcases, in a few words each,
 *   picked per role (provider persona review 2026-10-20: the things that convert each role lived only on
 *   /aba-case-finder and nobody clicked through). Ends with one text link to the case finder page, not a second CTA.
 *   "En español" switches the site language and the tracker chip opens the tracker page; both carry a small arrow so
 *   they read as tappable (persona round 2).
 * @module components/cityRole/CityHeroChips
 */
import { useTranslation } from "react-i18next";
import { AwardIcon, GlobeIcon, ShieldIcon, LockIcon, DollarIcon, UsersIcon, MonitorIcon, ArrowRightIcon, ChevronRightIcon } from "../Icons";
import { switchLanguage } from "../LanguageSwitcher";
import { seoHref } from "../../lib/seo";

const CHIPS = {
  noCert: { Icon: AwardIcon, key: "cityRole.chip.noCert", en: "No certification needed" },
  spanish: { Icon: GlobeIcon, key: "cityRole.chip.spanish", en: "En español", lang: true },
  everyCredential: { Icon: AwardIcon, key: "cityRole.chip.everyCredential", en: "BT to BCBA welcome" },
  payType: { Icon: DollarIcon, key: "cityRole.chip.payType", en: "Pay + W2/1099 up front" },
  tracker: { Icon: DollarIcon, key: "cityRole.chip.tracker", en: "Free hours & pay tracker", to: "free-aba-timesheet-payment-tracker" },
  caseload: { Icon: UsersIcon, key: "cityRole.chip.caseload", en: "Caseload up front" },
  supervision: { Icon: UsersIcon, key: "cityRole.chip.supervision", en: "BCBA supervision listed" },
  remote: { Icon: MonitorIcon, key: "cityRole.chip.remote", en: "Remote where licensed" },
  verified: { Icon: ShieldIcon, key: "cityRole.chip.verified", en: "NPI-verified agencies" },
  private: { Icon: LockIcon, key: "cityRole.chip.private", en: "Contact stays private" },
};

// Four per role: two that matter most to that role, then the two trust markers every provider asked about.
export const HERO_CHIPS_BY_ROLE = {
  bt: ["noCert", "spanish", "verified", "private"],
  rbt: ["tracker", "payType", "verified", "private"],
  bcba: ["caseload", "remote", "verified", "private"],
  bcaba: ["supervision", "payType", "verified", "private"],
  "aba-cases": ["everyCredential", "tracker", "verified", "private"],
};

export default function CityHeroChips({ roleKey, onNavigate, isMobile }) {
  const { t, i18n } = useTranslation();
  const ids = HERO_CHIPS_BY_ROLE[roleKey] || HERO_CHIPS_BY_ROLE["aba-cases"];
  const isEs = i18n.language?.startsWith("es");
  const chipStyle = { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 700, color: "#e2e8f0", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 99, padding: "7px 13px", fontFamily: "inherit" };
  return (
    <div data-testid="city-hero-chips" style={{ marginTop: 22 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxWidth: 640 }}>
        {ids.map((id) => {
          const c = CHIPS[id];
          // The Spanish chip offers the other language: "English" once the page is already in Spanish.
          const label = c.lang && isEs ? "English" : t(c.key, c.en);
          const inner = <><c.Icon size={14} color="#a5b4fc" />{label}</>;
          const tap = <>{inner}<ChevronRightIcon size={13} color="#a5b4fc" /></>;
          const tapStyle = { ...chipStyle, border: "1px solid rgba(165,180,252,0.55)", cursor: "pointer" };
          if (c.lang) return <button key={id} type="button" onClick={() => switchLanguage(i18n, isEs ? "en" : "es")} style={tapStyle}>{tap}</button>;
          if (c.to) return <a key={id} href={seoHref(c.to)} onClick={(e) => { e.preventDefault(); onNavigate(c.to); }} style={{ ...tapStyle, textDecoration: "none" }}>{tap}</a>;
          return <span key={id} style={chipStyle}>{inner}</span>;
        })}
      </div>
      <a
        href={seoHref("aba-case-finder")}
        onClick={(e) => { e.preventDefault(); onNavigate("aba-case-finder"); }}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: isMobile ? 14 : 16, fontSize: 14, fontWeight: 700, color: "#a5b4fc", textDecoration: "none" }}
      >
        {t("cityRole.howItWorks", "How ABAcases works")} <ArrowRightIcon size={14} color="#a5b4fc" />
      </a>
    </div>
  );
}
